/**
 * Gestão 2 — Theme & layout scripts.
 *
 * Replaces sidebar.js for the new gestao interface.
 * Pure JS (no Bootstrap dependency for sidebar/dropdowns).
 *
 * Features:
 * - Sidebar toggle (desktop fixed + mobile overlay)
 * - Submenu expand/collapse (pure JS)
 * - Active link management with URL matching
 * - Topbar dropdown menus (click-outside pattern)
 * - Theme selector (dark/light/color)
 * - Fullscreen toggle
 * - Focus mode
 * - Print
 * - Float page data (FAB + drawer)
 * - Lucide icon refresh after DOM changes
 *
 * @package Labead
 */

// ================================================================
// SIDEBAR TOGGLE
// ================================================================

const sidebar = document.getElementById('sidebar');
const backdrop = document.getElementById('sidebar-backdrop');
const toggler = document.getElementById('sidebar_toggle');

/**
 * Open sidebar (mobile overlay).
 */
function openSidebar() {
	if (!sidebar) return;
	sidebar.classList.remove('lw:-translate-x-full');
	sidebar.classList.add('lw:translate-x-0');
	if (backdrop) backdrop.classList.remove('lw:hidden');
	document.body.classList.add('sidebar-enable');
}

/**
 * Close sidebar (mobile overlay).
 */
function closeSidebar() {
	if (!sidebar) return;
	sidebar.classList.add('lw:-translate-x-full');
	sidebar.classList.remove('lw:translate-x-0');
	if (backdrop) backdrop.classList.add('lw:hidden');
	document.body.classList.remove('sidebar-enable');
}

/**
 * Check if viewport is mobile (< 1024px / lg breakpoint).
 *
 * @return {boolean}
 */
function isMobile() {
	return window.innerWidth < 1024;
}

if (toggler) {
	toggler.addEventListener('click', function () {
		if (!isMobile()) {
			// Desktop: collapse / expand sidebar.
			toggleDesktopSidebar();
		} else {
			// Mobile: overlay.
			if (sidebar.classList.contains('lw:-translate-x-full') || !sidebar.classList.contains('lw:translate-x-0')) {
				openSidebar();
			} else {
				closeSidebar();
			}
		}
	});
}

// ================================================================
// DESKTOP SIDEBAR COLLAPSE
// ================================================================

/**
 * Apply the collapsed visual state without persisting.
 * Used both by toggleDesktopSidebar() and the init restore.
 *
 * Desde 07/10/2026 a barra recolhida continua na tela, só com os ícones (como
 * na área do aluno): quem faz o desenho é o CSS de body.sidebar-collapsed
 * (_theme.scss). Antes as classes de translate/padding eram tiradas aqui e a
 * barra saía inteira.
 */
function applyDesktopSidebarCollapsed() {
	if (!sidebar) return;
	document.body.classList.add('sidebar-collapsed');
}

/**
 * Toggle the desktop sidebar between expanded and collapsed.
 */
function toggleDesktopSidebar() {
	if (document.body.classList.contains('sidebar-collapsed')) {
		document.body.classList.remove('sidebar-collapsed');
		try { localStorage.removeItem('lb_sidebar_collapsed'); } catch (e) { }
	} else {
		applyDesktopSidebarCollapsed();
		try { localStorage.setItem('lb_sidebar_collapsed', '1'); } catch (e) { }
	}
}

// Barra recolhida: tocar num item com submenu abre a barra para mostrar o submenu.
if (sidebar) {
	sidebar.addEventListener('click', function (e) {
		if (isMobile() || !document.body.classList.contains('sidebar-collapsed')) return;
		if (e.target.closest('[data-sidebar-toggle]')) {
			toggleDesktopSidebar();
		}
	}, true);
}

// Backdrop click closes sidebar.
if (backdrop) {
	backdrop.addEventListener('click', closeSidebar);
}

// Close sidebar on resize to desktop.
window.addEventListener('resize', function () {
	if (!isMobile() && document.body.classList.contains('sidebar-enable')) {
		closeSidebar();
	}
});

// ================================================================
// SIDEBAR SUBMENU TOGGLE
// ================================================================

/**
 * Open a sidebar submenu with slide-down animation.
 * Also recalculates max-height on all open ancestor submenus
 * so nested content is never clipped.
 *
 * @param {HTMLElement} submenu The [data-sidebar-submenu] element.
 * @param {HTMLElement|null} chevron Optional chevron icon.
 */
function openSubmenu(submenu, chevron) {
	submenu.classList.add('submenu-open');
	const childHeight = submenu.scrollHeight;
	submenu.style.maxHeight = childHeight + 'px';
	if (chevron) chevron.style.transform = 'rotate(90deg)';
	addAncestorHeights(submenu, childHeight);
}

/**
 * Close a sidebar submenu with slide-up animation.
 * Also recalculates max-height on all open ancestor submenus.
 *
 * @param {HTMLElement} submenu The [data-sidebar-submenu] element.
 * @param {HTMLElement|null} chevron Optional chevron icon.
 */
function closeSubmenu(submenu, chevron) {
	const childHeight = submenu.scrollHeight;
	submenu.classList.remove('submenu-open');
	submenu.style.maxHeight = '';
	if (chevron) chevron.style.transform = '';
	addAncestorHeights(submenu, -childHeight);
}

/**
 * Adjust max-height on all open ancestor [data-sidebar-submenu] elements
 * by adding delta (positive when opening, negative when closing).
 * This prevents parent submenus from clipping expanded child submenus.
 *
 * @param {HTMLElement} el    Starting element to walk up from.
 * @param {number}      delta Height change to propagate.
 */
function addAncestorHeights(el, delta) {
	let parent = el.parentElement?.closest('[data-sidebar-submenu]');
	while (parent) {
		if (parent.classList.contains('submenu-open')) {
			const current = parseFloat(parent.style.maxHeight) || parent.scrollHeight;
			parent.style.maxHeight = (current + delta) + 'px';
		}
		parent = parent.parentElement?.closest('[data-sidebar-submenu]');
	}
}

// Preserve legacy name for external callers.
var updateAncestorHeights = addAncestorHeights;

document.querySelectorAll('[data-sidebar-toggle]').forEach(function (btn) {
	btn.addEventListener('click', function (e) {
		e.preventDefault();
		e.stopPropagation();

		const targetId = btn.getAttribute('data-sidebar-toggle');
		const submenu = document.getElementById(targetId);
		if (!submenu) return;

		const chevron = btn.querySelector('[data-sidebar-chevron]');
		const isOpen = submenu.classList.contains('submenu-open');

		if (isOpen) {
			closeSubmenu(submenu, chevron);
		} else {
			openSubmenu(submenu, chevron);
		}
	});
});

// ================================================================
// ACTIVE LINK MANAGEMENT
// ================================================================

/**
 * Update active links in sidebar based on current URL.
 */
function updateActiveLinks() {
	const currentUrl = (window.location.pathname + window.location.search).replace(/\/$/, '');

	// Remove existing active classes.
	document.querySelectorAll('.sidebar-link.active').forEach(function (link) {
		link.classList.remove('active', 'lw:bg-white/15');
	});

	const sidebarLinks = document.querySelectorAll('.sidebar-link[href]');
	let bestMatch = null;
	let bestMatchLength = 0;

	sidebarLinks.forEach(function (link) {
		const href = link.getAttribute('href');
		if (!href || href === '#') return;

		let linkUrl;
		try {
			linkUrl = (href.startsWith('http') ? new URL(href).pathname : href).replace(/\/$/, '');
		} catch (e) {
			return;
		}

		// Exact match.
		if (linkUrl === currentUrl) {
			bestMatch = link;
			bestMatchLength = linkUrl.length;
		}
		// Partial match (current URL starts with link URL).
		else if (currentUrl.startsWith(linkUrl) && linkUrl.length > bestMatchLength) {
			const nextChar = currentUrl.charAt(linkUrl.length);
			if (nextChar === '/' || nextChar === '?' || nextChar === '#' || nextChar === '') {
				bestMatch = link;
				bestMatchLength = linkUrl.length;
			}
		}
	});

	if (bestMatch) {
		bestMatch.classList.add('active', 'lw:bg-white/15');

		// Collect all parent submenus (from innermost to outermost).
		const submenus = [];
		let parent = bestMatch.closest('[data-sidebar-submenu]');
		while (parent) {
			submenus.push(parent);
			parent = parent.parentElement?.closest('[data-sidebar-submenu]');
		}

		// Open submenus from outermost to innermost (reverse order).
		// This ensures parent containers have proper max-height before
		// calculating nested submenu heights.
		for (let i = submenus.length - 1; i >= 0; i--) {
			const submenu = submenus[i];
			const toggle = document.querySelector(`[data-sidebar-toggle="${submenu.id}"]`);
			const chevron = toggle ? toggle.querySelector('[data-sidebar-chevron]') : null;
			openSubmenu(submenu, chevron);
		}
	}
}

/**
 * Observe URL changes for SPA-style navigation.
 */
function initUrlObserver() {
	const origPush = history.pushState;
	const origReplace = history.replaceState;

	history.pushState = function () {
		origPush.apply(history, arguments);
		setTimeout(updateActiveLinks, 100);
	};

	history.replaceState = function () {
		origReplace.apply(history, arguments);
		setTimeout(updateActiveLinks, 100);
	};

	window.addEventListener('popstate', function () {
		setTimeout(updateActiveLinks, 100);
	});
}

// ================================================================
// TOPBAR DROPDOWN MENUS (pure JS click-outside pattern)
// ================================================================

document.querySelectorAll('[data-dropdown]').forEach(function (container) {
	const toggle = container.querySelector('[data-dropdown-toggle]');
	const menu = container.querySelector('[data-dropdown-menu]');
	if (!toggle || !menu) return;

	// Remove lw:hidden if present (CSS handles hidden state via opacity/visibility).
	menu.classList.remove('lw:hidden');

	toggle.addEventListener('click', function (e) {
		e.stopPropagation();

		// Close other dropdowns first.
		document.querySelectorAll('[data-dropdown-menu]').forEach(function (m) {
			if (m !== menu) m.classList.remove('open');
		});

		// Reset sub-dropdowns inside this container on every toggle click.
		container.querySelectorAll('[data-subdropdown]').forEach(function (sub) {
			sub.classList.remove('open');
		});

		menu.classList.toggle('open');
	});
});

// Click outside closes all dropdowns and sub-dropdowns.
document.addEventListener('click', function (e) {
	if (!e.target.closest('[data-dropdown]')) {
		document.querySelectorAll('[data-dropdown-menu]').forEach(function (m) {
			m.classList.remove('open');
		});
		document.querySelectorAll('[data-subdropdown]').forEach(function (s) {
			s.classList.remove('open');
		});
	}
});

// ================================================================
// SUB-DROPDOWN (inline accordion inside a parent dropdown)
// ================================================================

document.addEventListener('click', function (e) {
	const toggle = e.target.closest('[data-subdropdown-toggle]');
	if (!toggle) return;
	e.stopPropagation();

	const container = toggle.closest('[data-subdropdown]');
	if (!container) return;

	container.classList.toggle('open');
});

// ================================================================
// THEME SELECTOR
// ================================================================

function applyTheme(theme, persistCookie) {
	document.documentElement.setAttribute('data-bs-theme', theme);

	if (persistCookie) {
		document.cookie = 'lbtheme=' + theme + ';path=/;max-age=31536000;SameSite=Lax';
	}
}

document.querySelectorAll('[data-selector-theme]').forEach(function (btn) {
	btn.addEventListener('click', function () {
		const theme = btn.getAttribute('data-selector-theme');
		applyTheme(theme, true);

		// Close dropdown.
		const menu = btn.closest('[data-dropdown-menu]');
		if (menu) menu.classList.remove('open');
	});
});

// ================================================================
// FULLSCREEN
// ================================================================

document.querySelectorAll('[data-toggle="fullscreen"]').forEach(function (btn) {
	btn.addEventListener('click', function (e) {
		e.preventDefault();
		document.body.classList.toggle('fullscreen-enable');

		if (!document.fullscreenElement && !document.webkitFullscreenElement) {
			const el = document.documentElement;
			if (el.requestFullscreen) el.requestFullscreen();
			else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
		} else {
			if (document.exitFullscreen) document.exitFullscreen();
			else if (document.webkitCancelFullScreen) document.webkitCancelFullScreen();
		}
	});
});

document.addEventListener('fullscreenchange', handleFullscreenExit);
document.addEventListener('webkitfullscreenchange', handleFullscreenExit);

function handleFullscreenExit() {
	if (!document.fullscreenElement && !document.webkitIsFullScreen) {
		document.body.classList.remove('fullscreen-enable');
	}
}

// ================================================================
// FOCUS MODE
// ================================================================

const focusExitBtn = document.getElementById('focus-exit-btn');
const pageTopbar = document.getElementById('page-topbar');
const pageFooter = document.getElementById('page-footer');

/**
 * Toggle focus mode — hides topbar, sidebar and footer. Shows a small
 * floating exit button in the top-right corner.
 */
function toggleFocus() {
	const isFocused = document.body.getAttribute('data-layout') === 'lb-focus';

	if (isFocused) {
		// Exit focus.
		document.body.removeAttribute('data-layout');
		if (pageTopbar) pageTopbar.classList.remove('lw:hidden');
		if (sidebar) sidebar.classList.remove('lw:!hidden');
		if (pageFooter) pageFooter.classList.remove('lw:hidden');
		if (focusExitBtn) {
			focusExitBtn.classList.add('lw:opacity-0', 'lw:pointer-events-none');
			focusExitBtn.classList.remove('lw:opacity-100', 'lw:pointer-events-auto');
		}

		// Restore main padding.
		const main = document.querySelector('main');
		if (main) {
			main.classList.add('lw:pt-16', 'lw:lg:pl-64');
		}

		// Persist exit.
		try { localStorage.removeItem('lb_focus'); } catch (e) { }
	} else {
		// Enter focus.
		document.body.setAttribute('data-layout', 'lb-focus');
		if (pageTopbar) pageTopbar.classList.add('lw:hidden');
		if (sidebar) sidebar.classList.add('lw:!hidden');
		if (pageFooter) pageFooter.classList.add('lw:hidden');
		if (focusExitBtn) {
			focusExitBtn.classList.remove('lw:opacity-0', 'lw:pointer-events-none');
			focusExitBtn.classList.add('lw:opacity-100', 'lw:pointer-events-auto');
		}

		// Remove main padding (full area).
		const main = document.querySelector('main');
		if (main) {
			main.classList.remove('lw:pt-16', 'lw:lg:pl-64');
		}

		// Also close sidebar overlay if open on mobile.
		closeSidebar();

		// Persist entry.
		try { localStorage.setItem('lb_focus', '1'); } catch (e) { }
	}
}

document.querySelectorAll('.lb-focus').forEach(function (btn) {
	btn.addEventListener('click', function (e) {
		toggleFocus();
	});
});

// ================================================================
// PRINT
// ================================================================

let prePrintTheme = null;

function preparePrintTheme() {
	if (prePrintTheme !== null) return;
	prePrintTheme = document.documentElement.getAttribute('data-bs-theme') || 'color';
	applyTheme('light', false);
}

function restorePrintTheme() {
	if (prePrintTheme === null) return;
	applyTheme(prePrintTheme, false);
	prePrintTheme = null;
}

document.querySelectorAll('.lb-print').forEach(function (btn) {
	btn.addEventListener('click', function () {
		preparePrintTheme();
		setTimeout(function () {
			window.print();
		}, 1000);
	});
});

window.addEventListener('beforeprint', preparePrintTheme);
window.addEventListener('afterprint', restorePrintTheme);

// ================================================================
// FLOAT PAGE DATA (DRAWER)
// ================================================================

/**
 * Toggle the float-page-data drawer.
 * Elements are queried each time so references stay valid after htmx swaps.
 */
function toggleFloatDrawer() {
	const floatDrawer = document.getElementById('float-page-drawer');
	const floatBackdrop = document.getElementById('float-page-backdrop');
	if (!floatDrawer) return;

	const isOpen = !floatDrawer.classList.contains('lw:translate-x-full');

	// A sombra só existe aberto: fechado (fora da tela, à direita) ela vazava
	// como um degradê na borda da página.
	if (isOpen) {
		floatDrawer.classList.add('lw:translate-x-full');
		floatDrawer.classList.remove('lw:translate-x-0', 'lw:shadow-2xl');
		if (floatBackdrop) floatBackdrop.classList.add('lw:hidden');
	} else {
		floatDrawer.classList.remove('lw:translate-x-full');
		floatDrawer.classList.add('lw:translate-x-0', 'lw:shadow-2xl');
		if (floatBackdrop) floatBackdrop.classList.remove('lw:hidden');
	}
}

// Event delegation — works after htmx content swaps.
document.addEventListener('click', function (e) {
	if (e.target.closest('[data-float-page-toggle]')) {
		toggleFloatDrawer();
	}
});


// ================================================================
// ORG DROPDOWN — Sidebar school switcher + cross-tab sync
// ================================================================

// Track the org this tab loaded with (used by cross-tab sync).
const _lbOrgTabId = Math.random().toString(36).slice(2);
const _lbOrgOriginal = (document.cookie.match(/(?:^|;\s*)lb_org=([^;]*)/) || [])[1] || '';

// Clean ?org= / ?lb_org= from URL bar — PHP has already consumed them to set
// the escola context. Removing them prevents stale params from overriding the
// cookie if the user refreshes or switches org again on the same page.
(function () {
	const _u = new URL(location.href);
	if (_u.searchParams.has('org') || _u.searchParams.has('lb_org')) {
		_u.searchParams.delete('org');
		_u.searchParams.delete('lb_org');
		history.replaceState(null, '', _u.toString());
	}
}());

/**
 * Switch active organization cookie and reload.
 * Reused by sidebar and other gestao screens to avoid duplicated listeners.
 *
 * @param {string} orgSlug Organization slug.
 */
function switchOrg(orgSlug) {
	if (!orgSlug) return;

	const lbData = window.lb_data || {};
	const escolaHosts = lbData.escola_hosts || {};
	const mainSiteUrl = lbData.main_site_url || '';

	// If the target org has a custom domain different from the current host,
	// navigate there directly — it will auto-detect the escola and set the cookie.
	const targetHost = escolaHosts[orgSlug] || '';

	if (targetHost && targetHost !== location.hostname) {
		location.href = 'https://' + targetHost + location.pathname;
		return;
	}

	// If there is no custom domain for the target org but we are currently on a
	// custom escola domain, redirect back to the main site so the host-based
	// override does not force the wrong escola.
	const isOnCustomDomain = Object.values(escolaHosts).some(h => h === location.hostname);
	if (!targetHost && isOnCustomDomain && mainSiteUrl) {
		const orgId = (lbData.escola_ids || {})[orgSlug] || '';
		const orgParam = orgId ? '?org=' + encodeURIComponent(orgId) : '';
		location.href = mainSiteUrl + location.pathname + orgParam;
		return;
	}

	document.cookie = 'lb_org=' + encodeURIComponent(orgSlug) + '; path=/; SameSite=Lax';

	// Notify other tabs about org change.
	if (typeof BroadcastChannel !== 'undefined') {
		const channel = new BroadcastChannel('lb_org_sync');
		channel.postMessage({ org: orgSlug, tabId: _lbOrgTabId });
		channel.close();
	}

	// Navigate to a clean URL — strip ?org= / ?lb_org= so they don't override
	// the cookie we just set on the next load.
	const _cleanUrl = new URL(location.href);
	_cleanUrl.searchParams.delete('org');
	_cleanUrl.searchParams.delete('lb_org');
	// Mesma página (só o #aba muda ou nem isso — ex.: Estilo › #mascotes): atribuir a mesma URL
	// com âncora NÃO recarrega, e a troca não acontecia. Aí o reload é obrigatório.
	const _semAncora = (u) => u.split('#')[0];
	if (_semAncora(_cleanUrl.toString()) === _semAncora(location.href)) {
		location.reload();
		return;
	}
	location.href = _cleanUrl.toString();
}

// Sidebar org switch links (gestao).
document.addEventListener('click', function (e) {
	const link = e.target.closest('[data-org-switch]');
	if (!link) return;

	e.preventDefault();
	const orgSlug = link.getAttribute('data-org-switch');
	switchOrg(orgSlug);
});

/*---------------------------------------------------
	Cross-tab lb_org sync — show blocking overlay when escola changes in another tab
---------------------------------------------------*/
if (typeof BroadcastChannel !== 'undefined') {
	const lbOrgChannel = new BroadcastChannel('lb_org_sync');
	lbOrgChannel.onmessage = function (e) {
		const data = e.data || {};

		// Ignore messages from this same tab.
		if (data.tabId === _lbOrgTabId) return;

		// If the new org matches this tab's original org, dismiss any existing overlay.
		if (data.org === _lbOrgOriginal) {
			const existing = document.getElementById('lb-org-stale-overlay');
			if (existing) existing.remove();
			return;
		}

		// Prevent duplicate overlays.
		if (document.getElementById('lb-org-stale-overlay')) return;

		// Build blocking overlay — reuse lbAlert overlay+dialog styles.
		const overlay = document.createElement('div');
		overlay.id = 'lb-org-stale-overlay';
		overlay.className = 'lb-alert-overlay';

		// Build mascote message with two buttons.
		const color = 'w';
		const msg = '<h4>Atenção</h4>'
			+ '<p>A escola ativa foi trocada em outra aba.</p>'
			+ '<div class="lb-alert-actions">'
			+ '<button type="button" class="lb-alert-btn" data-lb-restore-org>Manter a escola desta aba</button>'
			+ '<button type="button" class="lb-alert-btn lb-alert-btn-confirm" data-lb-reload-org>Usar a nova escola</button>'
			+ '</div>';

		if (window.lb_data?.mascote_alert) {
			const html = window.lbMascote ? lbMascote.molde(color, msg, 'atencao') : '';
			const temp = document.createElement('div');
			temp.innerHTML = html;
			const mascote = temp.querySelector('.lbm-container');
			if (mascote) {
				mascote.classList.remove('lbm-fixed', 'lbm-inverte', 'lbm-close');
				mascote.classList.add('lb-alert-dialog');
				mascote.querySelectorAll('.lmsg-c, .lmsg-t').forEach(el => el.remove());
			}
			overlay.innerHTML = temp.innerHTML;
		} else {
			overlay.innerHTML = '<div style="background:var(--theme-surface-alt,#fff);color:var(--theme-on-surface-alt,#111827);border-radius:12px;padding:2rem;max-width:420px;text-align:center;">'
				+ '<p style="font-size:1.1rem;margin:0 0 1rem">A escola ativa foi trocada em outra aba.</p>'
				+ '<div style="display:flex;gap:.5rem;justify-content:center">'
				+ '<button data-lb-restore-org style="padding:.5rem 1.5rem;border-radius:6px;border:1px solid var(--theme-border,#d1d5db);background:transparent;color:inherit;cursor:pointer;font-size:.9rem;">Manter a escola desta aba</button>'
				+ '<button data-lb-reload-org style="padding:.5rem 1.5rem;border-radius:6px;border:none;background:#2563eb;color:#fff;cursor:pointer;font-size:.9rem;">Usar a nova escola</button>'
				+ '</div></div>';
		}

		document.body.appendChild(overlay);

		// Trigger open animation next frame.
		requestAnimationFrame(() => overlay.classList.add('is-open'));

		// "Usar a nova escola" — reload to use the new escola.
		overlay.querySelector('[data-lb-reload-org]').addEventListener('click', function () {
			location.reload();
		});

		// "Manter a escola desta aba" — restore original cookie and reload.
		// A reload (not just a client-side cookie write) is required: the lb_org
		// cookie is shared across all tabs of this origin, and PHP re-issues it
		// on every request from whatever value that request carried
		// (Gestao::set_cookie(), called on every 'init'). Without a round-trip
		// through the server here, a stray request from another tab (e.g. the
		// other tab's own reload, or a background heartbeat) can still be
		// in flight with the altered org and silently clobber this restore
		// before the next refresh.
		overlay.querySelector('[data-lb-restore-org]').addEventListener('click', function () {
			if (_lbOrgOriginal) {
				document.cookie = 'lb_org=' + _lbOrgOriginal + '; path=/; SameSite=Lax';
				// Broadcast the restore so other tabs know.
				const ch = new BroadcastChannel('lb_org_sync');
				ch.postMessage({ org: _lbOrgOriginal, tabId: _lbOrgTabId });
				ch.close();
				location.reload();
				return;
			}
			overlay.remove();
		});
	};
}

// ================================================================
// INITIALIZATION
// ================================================================

document.addEventListener('DOMContentLoaded', function () {
	// Restore desktop sidebar collapsed state.
	try {
		if (!isMobile() && localStorage.getItem('lb_sidebar_collapsed') === '1') {
			applyDesktopSidebarCollapsed();
		}
	} catch (e) { }

	// Sync focus mode: if the inline <head> script set data-layout on <html>,
	// transfer it to <body> and apply JS state (button visibility, padding).
	if (document.documentElement.getAttribute('data-layout') === 'lb-focus') {
		document.documentElement.removeAttribute('data-layout');
		document.body.setAttribute('data-layout', 'lb-focus');

		// Classes already hidden by CSS (html[data-layout] rule) — now apply
		// the JS-managed state so toggling works correctly afterwards.
		if (pageTopbar) pageTopbar.classList.add('lw:hidden');
		if (sidebar) sidebar.classList.add('lw:!hidden');
		if (pageFooter) pageFooter.classList.add('lw:hidden');

		const main = document.querySelector('main');
		if (main) main.classList.remove('lw:pt-16', 'lw:lg:pl-64');

		if (focusExitBtn) {
			focusExitBtn.classList.remove('lw:opacity-0', 'lw:pointer-events-none');
			focusExitBtn.classList.add('lw:opacity-100', 'lw:pointer-events-auto');
		}
	}

	// Active links — delayed to ensure icons are rendered.
	setTimeout(updateActiveLinks, 150);
});

// ================================================================
// PUBLIC API
// ================================================================

window.sidebarUtils = {
	updateActiveLinks: updateActiveLinks,
	openSidebar: openSidebar,
	closeSidebar: closeSidebar,
	isMobile: isMobile,
	toggleFloatDrawer: toggleFloatDrawer,
	toggleFocus: toggleFocus,
	toggleDesktopSidebar: toggleDesktopSidebar,
	switchOrg: switchOrg,
};
